import {
  useState,
  useEffect,
  useCallback,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type RefObject,
} from 'react';

interface UseResizableOptions {
  /** Edge carrying the resize handle; left suits a right-hand panel. */
  edge?: 'left' | 'right';
  minWidth?: number;
  maxWidth?: number;
  step?: number;
  initialWidth?: number;
  width?: number;
  onWidthChange?: (width: number) => void;
}

const DEFAULT_MIN_WIDTH = 280;
const DEFAULT_MAX_WIDTH = 600;
const DEFAULT_STEP = 20;
const DEFAULT_INITIAL_WIDTH = 384;

export function useResizable(
  panelRef: RefObject<HTMLDivElement | null>,
  options?: UseResizableOptions,
) {
  const edge = options?.edge ?? 'left';
  const minWidth = options?.minWidth ?? DEFAULT_MIN_WIDTH;
  const maxWidth = options?.maxWidth ?? DEFAULT_MAX_WIDTH;
  const resizeStep = options?.step ?? DEFAULT_STEP;

  const isControlled = options?.width !== undefined;
  const [internalWidth, setInternalWidth] = useState(
    options?.initialWidth ?? DEFAULT_INITIAL_WIDTH,
  );
  const width = isControlled ? (options?.width ?? 0) : internalWidth;
  const setWidth = useCallback(
    (value: number | ((prev: number) => number)) => {
      if (isControlled && options?.onWidthChange) {
        const next =
          typeof value === 'function' ? value(options?.width ?? 0) : value;
        options.onWidthChange(next);
      } else {
        setInternalWidth(value);
      }
    },
    [isControlled, options],
  );
  const [isResizing, setIsResizing] = useState(false);

  const handleMouseDown = useCallback((e: ReactMouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const direction = e.key === 'ArrowRight' ? 1 : -1;
        const delta = direction * (edge === 'right' ? resizeStep : -resizeStep);
        setWidth((prev) =>
          Math.min(maxWidth, Math.max(minWidth, prev + delta)),
        );
      }
    },
    [edge, resizeStep, minWidth, maxWidth, setWidth],
  );

  useEffect(() => {
    if (!isResizing) return undefined;

    const handleMouseMove = (e: MouseEvent) => {
      // The release may happen outside the window, where our mouseup
      // listener cannot observe it. A later unpressed move ends the drag.
      if ((e.buttons & 1) === 0) {
        setIsResizing(false);
        return;
      }
      if (!panelRef.current) return;
      const panelRect = panelRef.current.getBoundingClientRect();
      const newWidth =
        edge === 'right'
          ? e.clientX - panelRect.left
          : panelRect.right - e.clientX;
      setWidth(Math.min(maxWidth, Math.max(minWidth, newWidth)));
    };

    const handleMouseUp = () => setIsResizing(false);

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('blur', handleMouseUp);

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('blur', handleMouseUp);
    };
  }, [edge, isResizing, panelRef, minWidth, maxWidth, setWidth]);

  return {
    width,
    minWidth,
    maxWidth,
    isResizing,
    handleMouseDown,
    handleKeyDown,
  };
}
